/**
 * @file Formulations.js
 * @version 3.12.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-05
 * @modified 2026-10-14
 * @description Formulations — inventory items that have feed recipe data,
 *              stored as dated entries (iterations), each in the contract
 *              pricing period its date falls in.
 *
 *              The table lists every product with a formulation, once, at its
 *              newest entry — the entry's date and period are on the row. There
 *              is no period filter (Cam, 2026-10-14): an earlier entry is chosen
 *              from the date dropdown in the view modal, which mirrors the PDF
 *              layout. The archived toggle filters archived INVENTORY ITEMS —
 *              formulations themselves can't be archived, only deleted.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './Formulations.css';
import Input from './Input';
import Dropdown from './Dropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import TickCheckbox from './TickCheckbox';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import AuditHistoryModal from './AuditHistoryModal';
import FormulationModal from './FormulationModal';
import { formatCurrency } from '../utils/currencyFormatter';
import FormulationExportModal from './FormulationExportModal';
import ItemStatusBadge from './ItemStatusBadge';
import ConfirmSupplierCodeModal from './ConfirmSupplierCodeModal';
import PromptDialog from './PromptDialog';
import CustomerBlendOrganiser from './CustomerBlendOrganiser';
import useCustomerBlends from '../hooks/useCustomerBlends';
import MoveFormulationDialog from './MoveFormulationDialog';
import NewCustomerFormulationModal, { useNewCustomerFormulationAccess } from './NewCustomerFormulationModal';
import EntityLink from './EntityLink';
import { useSlideOutButton } from './SlideOutButton';
import useTableContextMenu from '../hooks/useTableContextMenu';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import SearchHelp from './SearchHelp';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { getAccountName } from '../utils/accountCodes';
import { formatFormulationDay, londonDayOf } from '../utils/formulationDates';

const PER_PAGE_OPTIONS = [25, 50, 100, 200].map(n => ({ value: n, label: `${n} per page` }));

const DEFAULT_FILTERS = { search: '', productTypes: [], showArchived: false };

/* Product type, not chart-of-accounts code. 202 and 207A are both blends —
   207A is the bagged ones — and nobody outside the ledger thinks of them as two
   different kinds of product. The server maps these back to codes. */
const PRODUCT_TYPE_OPTIONS = [
  { value: 'blend', label: 'Blends' },
  { value: 'compound', label: 'Compounds' },
  // Milk powder (200P) used to be swept in with compounds by a NOT IN, so there
  // was no way to look at it on its own.
  { value: 'milk_powder', label: 'Milk Powder' }
];

/** The date of the entry a row shows — the user's date, DD/MM/YYYY. */
const rowEntryDate = (row) => formatFormulationDay(row.iteration_date || londonDayOf(row.iteration_created_at));

function Formulations() {
  // merge: a tab that visited this page before the salesAccounts → productTypes
  // rename still holds the old filter object, and reading .length off the key
  // it does not have is what broke search after that deploy.
  const [filters, setFilters] = usePersistedState('formulations.filters', DEFAULT_FILTERS, { merge: true });
  /* No field means the server's grouped order — customer, then blend line, then
     item code — which is what this screen is for. A column header still sorts,
     and the grouping stays underneath it so a customer's rows keep together. */
  const [sorting, setSorting] = usePersistedState('formulations.sorting', {
    field: '',
    direction: 'asc'
  });
  const [pagination, setPagination] = usePersistedState('formulations.pagination', {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [openModal, setOpenModal] = usePersistedState('formulations.openModal', null);

  const [rows, setRows] = useState([]);

  /**
   * Calculated costs for the blends on screen: spot and the current period.
   *
   * Fetched after the rows rather than joined into the search — costing a
   * formulation is a multi-table aggregate and only blends can be costed at all,
   * so putting it in the search query would slow every page for every user.
   *
   * Two requests because the endpoint costs one price column per call; keeping it
   * that way leaves the shape stable for its other callers.
   */
  const [calc, setCalc] = useState({ spot: {}, current: {} });
  const [components, setComponents] = useState([]);
  // Only what an export sheet is headed with when it comes from the table.
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exportIds, setExportIds] = useState(null);
  const [exportPeriod, setExportPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: null
  });

  useSlideOutButton([filters.showArchived, selectedIds.length]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const ctxMenu = useTableContextMenu({ rows, selectedIds: selectedIdSet, getRowId: (r) => r.id });

  // ─── Registry and the current period ──────────────────────────────────────
  const fetchComponents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/formulations/components`, { credentials: 'include' });
      const data = await res.json();
      setComponents(data.components || []);
    } catch (err) {
      console.error('Error loading formulation components:', err);
    }
  }, []);

  const fetchPeriods = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/formulations/periods`, { credentials: 'include' });
      const data = await res.json();
      setCurrentPeriod(data.current_period || null);
    } catch (err) {
      console.error('Error loading formulation periods:', err);
    }
  }, []);

  useEffect(() => {
    fetchComponents();
    fetchPeriods();
  }, [fetchComponents, fetchPeriods]);

  // ─── Search ───────────────────────────────────────────────────────────────
  // Responses come back out of order; only the newest may write the rows.
  const latest = useLatestRequest();

  const search = useCallback(async () => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...(filters.showArchived ? { archived: 'true' } : {}),
        sortField: sorting.field,
        sortDirection: sorting.direction,
        page: String(pagination.page),
        limit: String(pagination.limit)
      });
      if (filters.search) params.set('search', filters.search);
      if (filters.productTypes.length) params.set('productTypes', filters.productTypes.join(','));

      const res = await fetch(`${API_URL}/api/formulations/search?${params}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search
      if (!res.ok) throw new Error(data.error || 'Failed to load formulations');
      setRows(data.formulations || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0
      }));
    } catch (err) {
      if (!latest.isLatest(seq)) return;
      console.error('Error searching formulations:', err);
      setRows([]);
      setAlertDialog({ isOpen: true, message: err.message, type: 'error' });
    } finally {
      if (latest.isLatest(seq)) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sorting, pagination.page, pagination.limit]);

  useEffect(() => {
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter/sort change goes back to page 1. Skipped on first render
  // so a restored page number survives navigating back to the page.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sorting]);

  // Selections are row-scoped — drop any that are no longer on screen so a
  // bulk action can't act on rows the user can't see.
  useEffect(() => {
    setSelectedIds(prev => {
      const visible = new Set(rows.map(r => r.id));
      const next = prev.filter(id => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [rows]);

  const handleFilterChange = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

  // Cost the blends on this page. 202/207A only — everything else has no bridge
  // to priced materials and would be a guaranteed miss.
  useEffect(() => {
    const ids = (rows || [])
      .filter((r) => ['202', '207A'].includes(String(r.sales_account_code || '').toUpperCase()))
      .map((r) => r.inventory_item_id)
      .filter(Boolean);
    if (!ids.length) { setCalc({ spot: {}, current: {} }); return undefined; }

    let cancelled = false;
    const base = `${API_URL}/api/blends/formulation-cost?itemIds=${ids.join(',')}`;
    Promise.all([
      fetch(`${base}&column=spot`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null),
      // No column = the endpoint picks the current period itself.
      fetch(base, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null)
    ]).then(([spot, current]) => {
      if (cancelled) return;
      // Silent on failure: a user without blend_costs gets 403, and blank columns
      // are the right outcome rather than an error on the formulations screen.
      setCalc({ spot: spot?.costs || {}, current: current?.costs || {} });
    });
    return () => { cancelled = true; };
  }, [rows]);

  /**
   * One row's three figures. Returns null for a row that can't be costed, so the
   * cells render empty rather than as a misleading zero.
   */
  const costsFor = useCallback((row) => {
    const id = row.inventory_item_id;
    const spot = calc.spot[id] || calc.spot[String(id)] || null;
    const current = calc.current[id] || calc.current[String(id)] || null;
    if (!spot && !current) return null;
    const recorded = row.purchase_unit_price === null || row.purchase_unit_price === undefined
      ? null : Number(row.purchase_unit_price);
    // Compare against the CURRENT period figure: that is what the product costs
    // to make now, where spot is a one-off buying price.
    const ref = current?.cost ?? null;
    const gapPct = ref && recorded ? ((ref - recorded) / recorded) * 100 : null;
    return {
      spot: spot?.cost ?? null,
      current: ref,
      recorded,
      coverage: current?.coverage_pct ?? spot?.coverage_pct ?? null,
      // Only flag where the calculation is trustworthy — a partial figure is a
      // known understatement and flagging it would cry wolf.
      flagged: gapPct !== null && Math.abs(gapPct) > 5 && (current?.coverage_pct ?? 0) >= 95,
      gapPct
    };
  }, [calc]);

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSorting({ field: 'item_code', direction: 'asc' });
  };

  // Any header filter lights Clear Filters.
  const headerFilters = activeHeaderFilters(
    filters, DEFAULT_FILTERS,
    { labels: { productTypes: 'Product type' } }
  );
  const filtersApplied = headerFilters.count > 0;

  const changePage = (page) => setPagination(prev => ({ ...prev, page }));
  const changeLimit = (limit) => setPagination(prev => ({ ...prev, limit, page: 1 }));

  // ─── Selection ────────────────────────────────────────────────────────────
  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  /**
   * Group the flat result into Customer Blends, preserving the server's sort
   * within each group.
   *
   * A compound is re-formulated in place and has no customer blend, so it stays
   * a plain row. A blend belongs to one, and its generations sit under it —
   * newest first, because the server orders by when each was first sold.
   * Blends the backfill could not place (sold to several customers, or never
   * invoiced) collect under one "Unassigned" group rather than vanishing.
   */
  const grouped = useMemo(() => {
    const byBlend = new Map();
    const loose = [];
    for (const row of rows) {
      if (row.scope !== 'blend') { loose.push(row); continue; }
      const key = row.customer_blend_id ?? 'unassigned';
      if (!byBlend.has(key)) {
        byBlend.set(key, {
          key,
          id: row.customer_blend_id ?? null,
          name: row.customer_blend_name || 'Unassigned',
          account: row.customer_blend_account || null,
          accountNo: row.customer_blend_account_no || null,
          accountId: row.customer_blend_account_id || null,
          generations: []
        });
      }
      byBlend.get(key).generations.push(row);
    }
    // Unassigned last: it is a to-do list, not a customer.
    const blends = [...byBlend.values()].sort((a, b) => {
      if (a.key === 'unassigned') return 1;
      if (b.key === 'unassigned') return -1;
      return (a.account || '').localeCompare(b.account || '') || a.name.localeCompare(b.name);
    });
    return { blends, loose };
  }, [rows]);

  /* Collapsed is the default: a customer with 22 generations turns the table
     into a wall, and the question on opening this screen is almost always
     "which customer", not "which generation". Tracked as the set that IS open
     so a new group arrives closed rather than surprising someone. */
  const [expanded, setExpanded] = usePersistedState('formulations.expandedBlends', []);
  const expandedSet = useMemo(() => new Set(expanded), [expanded]);
  const toggleCollapsed = (key) =>
    setExpanded(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  /* Every group in the dataset, not just this page's. A key for a group that is
     not currently rendered is harmless — it simply arrives already open when
     the page or filter brings it into view, which is the point. */
  const allBlendKeys = useMemo(
    () => [...new Set(rows.filter(r => r.scope === 'blend')
      .map(r => r.customer_blend_id ?? 'unassigned'))],
    [rows]
  );
  const allExpanded = allBlendKeys.length > 0
    && allBlendKeys.every(k => expandedSet.has(k));
  const toggleAll = () => setExpanded(allExpanded ? [] : allBlendKeys)

  const allSelected = rows.length > 0 && rows.every(r => selectedIds.includes(r.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : rows.map(r => r.id));

  // ─── Actions ──────────────────────────────────────────────────────────────
  const refresh = useCallback(() => {
    search();
    fetchPeriods();
  }, [search, fetchPeriods]);

  const handleDelete = (row) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Formulation',
      message: `Permanently delete the formulation for ${row.item_code} — ${row.item_name}, including every dated entry it has? This cannot be undone.`,
      type: 'error',
      confirmText: 'Delete',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const res = await fetch(`${API_URL}/api/formulations/${row.id}`, {
            method: 'DELETE', credentials: 'include'
          });
          if (!res.ok) throw new Error((await res.json()).error || 'Request failed');
          refresh();
        } catch (err) {
          setAlertDialog({ isOpen: true, message: err.message, type: 'error' });
        }
      }
    });
  };

  // Every action renders in every row — inapplicable ones are disabled, never
  // omitted, so the actions column never changes width between row types.
  const [confirmCodeFor, setConfirmCodeFor] = useState(null);
  const [newGenerationFor, setNewGenerationFor] = useState(null);
  const [renamingBlend, setRenamingBlend] = useState(null);
  const [movingRow, setMovingRow] = useState(null);
  const [organising, setOrganising] = useState(false);
  /* "New formulation for a customer" — item, formulation and placement in one
     save, offered from Add Formulation for the item that does not exist yet.
     Not persisted: it is a draft, and opens fresh. */
  const [newForCustomer, setNewForCustomer] = useState(false);
  const newForCustomerAccess = useNewCustomerFormulationAccess();

  /* The same two lists the organiser works from, so the badge and the modal can
     never disagree. Reloaded when the modal reports a change, and when the table
     refreshes — a formulation moved from a row action changes this count too. */
  const { unassigned: cbUnassigned, reload: reloadCustomerBlends } = useCustomerBlends();

  /* Unassigned only. A line called "Blend" used to be counted as a catch-all to
     tidy, but "Blend" is an accepted default name — a customer whose untyped
     generations live under it is filed correctly, not waiting on anyone. The
     badge is a backlog, and a number that never reaches zero because it counts
     something nobody intends to change is a number people stop reading. */
  const organiseCount = cbUnassigned?.length || 0;

  /**
   * Start the next generation of a customer blend: the newest formulation is
   * copied onto a NEW local-only item, ready to be edited and sent to the
   * supplier. It is deliberately not created in Xero yet — the supplier's code
   * becomes ours, and item_code is the join key everywhere, so it is written
   * once at confirmation rather than pushed provisionally and renamed.
   */
  /** Rename a blend line. The formulations inside keep their own names. */
  const renameBlend = async (blend, value) => {
    const name = String(value || '').trim();
    setRenamingBlend(null);
    if (!name || name === blend.name) return;
    try {
      const res = await fetch(`${API_URL}/api/formulations/customer-blends/${blend.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Rename failed');
      refresh();
    } catch (err) {
      setAlertDialog({ isOpen: true, message: err.message, type: 'error' });
    }
  };

  const startNewGeneration = async (blend, provisionalName) => {
    try {
      const res = await fetch(`${API_URL}/api/formulations/customer-blends/${blend.id}/new-generation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provisional_name: provisionalName })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setNewGenerationFor(null);
      await refresh();
      // Straight into editing it — the copy is the starting point, not the end.
      setOpenModal({ mode: 'edit', id: data.formulation.id });
    } catch (err) {
      setAlertDialog({ isOpen: true, type: 'error', message: `Could not start a new formulation: ${err.message}` });
    }
  };


  /**
   * Retire or restore a generation HERE. Deliberately never touches is_archived,
   * which mirrors Xero: a blend can be archived in Xero while still being the
   * live one, which is exactly the case for Chapple Farm.
   */
  const handleLocalArchive = async (row, next) => {
    try {
      const res = await fetch(`${API_URL}/api/formulations/items/${row.inventory_item_id}/local-archive`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_locally_archived: next })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Request failed');
      refresh();
    } catch (err) {
      setAlertDialog({ isOpen: true, type: 'error', message: `Could not update this formulation: ${err.message}` });
    }
  };

  const buildRowActions = useCallback((row) => {
    const isBlend = row.scope === 'blend';
    const locallyArchived = !!row.is_locally_archived;
    return [
      { key: 'view', icon: 'visibility', label: 'View', onClick: () => setOpenModal({ mode: 'view', id: row.id }) },
      { key: 'edit', icon: 'edit', label: 'Edit', onClick: () => setOpenModal({ mode: 'edit', id: row.id }) },
      { key: 'export', icon: 'ios_share', label: 'Export PDF', onClick: () => setExportIds([row.id]) },
      // Only a blend awaiting its supplier's code can take one. Rendered
      // disabled rather than hidden so the action set stays the same shape on
      // every row — see the table-actions rule in CLAUDE.md.
      {
        key: 'confirm-code', icon: 'assignment_turned_in', label: 'Confirm supplier code',
        disabled: !row.awaiting_supplier_code,
        title: row.awaiting_supplier_code
          ? "Enter the supplier's item code and create this in Xero"
          : 'Only a blend still waiting on its supplier code needs this',
        onClick: () => { if (row.awaiting_supplier_code) setConfirmCodeFor(row); }
      },
      {
        key: 'local-archive',
        icon: locallyArchived ? 'unarchive' : 'archive',
        label: locallyArchived ? 'Restore here' : 'Retire here',
        disabled: !isBlend,
        title: !isBlend
          ? 'Compounds are re-formulated in place, so there is nothing to retire'
          : locallyArchived
            ? 'Put this generation back into use here'
            : 'Retire this generation here. Does not touch Xero.',
        onClick: () => { if (isBlend) handleLocalArchive(row, !locallyArchived); }
      },
      // A blend that lands under the wrong farm — or under none — is the
      // common case this screen exists to fix, so it belongs on the row rather
      // than only inside the organiser.
      {
        key: 'move', icon: 'move_group', label: 'Assign to customer\u2026',
        disabled: !isBlend,
        title: isBlend
          ? 'Put this formulation under a customer, existing line or new'
          : 'Only blends are grouped by customer',
        onClick: () => { if (isBlend) setMovingRow(row); }
      },
      { key: 'history', icon: 'history', label: 'View history', onClick: () => setHistoryFor(row) },
      { key: 'delete', icon: 'delete', label: 'Delete', className: 'action-danger', onClick: () => handleDelete(row) }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const bulkActions = useCallback((ids) => [
    {
      key: 'bulk-export', icon: 'picture_as_pdf',
      label: `Export ${ids.size} to PDF`,
      onClick: () => setExportIds([...ids])
    }
  ], []);

  // ─── Derived ──────────────────────────────────────────────────────────────
  // Account display names for the rows being exported. The server has no
  // chart-of-accounts table, so it takes the labels from here rather than
  // duplicating the accountCodes map.
  const exportAccountNames = useMemo(() => {
    if (!exportIds) return {};
    const wanted = new Set(exportIds);
    const out = {};
    rows.forEach(r => {
      if (!wanted.has(r.id) || !r.sales_account_code) return;
      const name = getAccountName(r.sales_account_code);
      if (name) out[r.sales_account_code] = name;
    });
    return out;
  }, [exportIds, rows]);

  const sortIcon = (field) => {
    if (sorting.field !== field) return null;
    return (
      <span className="material-symbols-rounded sort-icon">
        {sorting.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
      </span>
    );
  };

  /**
   * The generation columns.
   *
   * One definition used by every nested table and by the compounds table, so a
   * column added to one cannot go missing from the other — which is exactly how
   * two tables that look the same drift apart.
   */
  const generationHead = (
    <tr>
      <th className="fm-check-col"></th>
      <th className="sortable" onClick={() => handleSort('item_code')}>
        Product {sortIcon('item_code')}
      </th>
      <th className="sortable" onClick={() => handleSort('sales_account_code')}>
        Sales account {sortIcon('sales_account_code')}
      </th>
      {/* Deliberately not sortable. These are computed for the rows on screen,
          so a sort would only order the current page while appearing to order
          the whole result set — worse than no sort. */}
      <th className="num">Spot</th>
      <th className="num">
        Current
        <span className="material-symbols-rounded fm-th-info"
              title="Today's cost of this recipe, priced at each material's latest spot price. Moves as the market moves — it is not what the blend was costed at when it was made.">
          info
        </span>
      </th>
      <th className="num">
        Inventory
        <span className="material-symbols-rounded fm-th-info"
              title="The same recipe priced at the purchase price held on each material's inventory item in Xero. Steadier than spot, and the figure the item's own cost is based on.">
          info
        </span>
      </th>
      {/* The date of the entry the row shows — its newest — and the period it
          falls in, so the table says how old each recipe is. */}
      <th className="sortable" onClick={() => handleSort('iteration_date')}>
        Formulation date {sortIcon('iteration_date')}
      </th>
      <th className="actions-col"></th>
    </tr>
  );

  /**
   * One formulation row. Shared by grouped generations and by compounds, which
   * have no customer blend — same columns either way, so the table reads as one
   * table rather than two stitched together.
   */
  const renderRow = (row) => {
    const isSelected = selectedIds.includes(row.id);
    return (

                    <tr
                      key={row.id}
                      className={`${row.item_archived ? 'fm-archived-row' : ''}${isSelected ? ' selected' : ''}`}
                      onContextMenu={ctxMenu.onRowContextMenu(row)}
                      onClick={() => setOpenModal({ mode: 'view', id: row.id })}
                    >
                      {/* Checkbox and actions cells stop the click bubbling —
                          selecting a row or opening its menu shouldn't also
                          open the view modal. */}
                      <td className="fm-check-col" onClick={(e) => e.stopPropagation()}>
                        <TickCheckbox
                          id={`fm-${row.id}`}
                          checked={isSelected}
                          onChange={() => toggleSelect(row.id)}
                          ariaLabel={`Select ${row.item_code}`}
                        />
                      </td>
                      <td>
                        <span className="name-over-badge">
                          <div style={{ display: "flex", justifyContent: "start", alignItems: "center", gap: "5px" }}>
                          <EntityLink type="inventory_item" id={row.inventory_item_id}
                                      className="fm-item-name" title={row.item_name}>
                            {row.item_name}
                          </EntityLink>
                          <span style={{ textTransform: "uppercase" }}>
                            <ItemStatusBadge item={row} />
                          </span>
                          </div>
                          <EntityLink type="inventory_item" id={row.inventory_item_id}
                                      variant="badge" className="account-badge-inline">
                            {row.item_code}
                          </EntityLink>
                        </span>
                      </td>
                      <td>
                        <span className="name-with-badge">
                          <span className={`account-badge-inline${!row.sales_account_code ? ' empty' : ''}`}>
                            {row.sales_account_code}
                          </span>
                          <span className="fm-account-name">
                            {getAccountName(row.sales_account_code) || ''}
                          </span>
                        </span>
                      </td>
                      {(() => {
                const c = costsFor(row);
                const cell = (v, extra = '') => (
                  <td className={`fm-cost ${extra}`}>
                    {v === null || v === undefined ? '—' : formatCurrency(v)}
                  </td>
                );
                if (!c) {
                  // Not a blend, or nothing priced: three empty cells rather than
                  // zeros, which would read as "costs nothing".
                  return <>{cell(null)}{cell(null)}{cell(null)}</>;
                }
                return (
                  <>
                    {cell(c.spot)}
                    <td className={`fm-cost${c.flagged ? ' fm-cost-flag' : ''}`}
                        title={c.coverage !== null
                          ? `Covers ${c.coverage}% of the recipe${c.gapPct !== null
                              ? ` · ${c.gapPct > 0 ? '+' : ''}${c.gapPct.toFixed(1)}% vs the cost price` : ''}`
                          : undefined}>
                      {c.current === null ? '—' : formatCurrency(c.current)}
                      {c.coverage !== null && c.coverage < 99.5 && (
                        <span className="fm-cost-partial">{c.coverage}%</span>
                      )}
                    </td>
                    {cell(c.recorded)}
                  </>
                );
              })()}
              <td className="fm-updated">
                <span className="fm-entry-date">{rowEntryDate(row)}</span>
                {row.iteration_period && (
                  <span className="fm-entry-period">{row.iteration_period}</span>
                )}
              </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="actions-cell">
                          <RowActionsMenu actions={buildRowActions(row)} />
                        </div>
                      </td>
                    </tr>
    );
  };

  return (
    <div className="search-layout formulations" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Formulations</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>
              Every product with recipe data, at its newest entry — the date and period are on each row.
              Open one to view its formulation and choose an earlier date.
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            {/* The badge is a SIBLING of the button, not a child: .btn-slideout
                is overflow:hidden so the label can slide out from under the
                icon, and anything inside it is clipped to the collapsed 45px
                circle. The wrapper is the positioning context instead. */}
            <div className="fm-organise-btn">
              <button className="btn-slideout" onClick={() => setOrganising(true)}
                      title={organiseCount
                        ? `${organiseCount} formulation${organiseCount === 1 ? '' : 's'} with no customer — place them here`
                        : 'Place unassigned formulations and tidy blend lines'}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">account_tree</span>
                </div>
                <span className="btn-label">Organise Blends</span>
              </button>
              {organiseCount > 0 && (
                <span className="fm-organise-badge" aria-hidden="true">{organiseCount}</span>
              )}
            </div>
            <div>
              <button className="btn-slideout" onClick={() => setOpenModal({ mode: 'add', id: null })}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add Formulation</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row" style={{ gridTemplateColumns: '2fr 1.6fr 1.1fr' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['item code', 'product', 'customer', 'account no.', 'customer blend']} />
            </label>
            <Input
              type="text"
              placeholder="Search product, customer or account no..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onEnter={() => search()}
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Product type</label>
            <MultiSelectDropdown
              options={PRODUCT_TYPE_OPTIONS}
              value={filters.productTypes}
              onChange={(v) => handleFilterChange('productTypes', v)}
              selectedInMenu
              hideTags
              placeholder="Any product"
            />
          </div>
          <div className="filter-group">
            <label>Per page</label>
            <Dropdown
              options={PER_PAGE_OPTIONS}
              value={pagination.limit}
              onChange={changeLimit}
            />
          </div>
        </div>
      </form>

      <div className="search-content">
        {selectedIds.length > 0 && (
          <div className="bulk-actions-bar">
            <div className="bulk-actions-info">
              <span className="bulk-actions-title">Bulk Actions:</span>
              <span>{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</span>
            </div>
            <div className="bulk-actions-buttons slideout">
              <div>
                <button className="btn-slideout" onClick={() => setExportIds([...selectedIds])}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">ios_share</span>
                  </div>
                  <span className="btn-label">Export PDF</span>
                </button>
              </div>
              <div>
                <button data-read-safe className="btn-slideout" onClick={() => setSelectedIds([])}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                  <span className="btn-label">Clear</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="table-toolbar">
          {/* The same labelled button Search POs and Search Bills use — an
              unlabelled icon here made the one screen with a two-level table the
              one screen where you had to guess what the control did. */}
          {grouped.blends.length > 0 && (
            <button className="btn-secondary btn-sm fm-expand-all" onClick={toggleAll}
                    title={allExpanded ? 'Collapse every customer' : 'Expand every customer'}>
              <span className="material-symbols-rounded">
                {allExpanded ? 'unfold_less' : 'unfold_more'}
              </span>
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          )}
          {pagination.totalPages > 0 && (
            <PaginationControls pagination={pagination} onPageChange={changePage} />
          )}
        </div>

        <div className="table-container">
          {!loading && rows.length === 0 ? (
            <div className="no-results">
              <span className="material-symbols-rounded">science</span>
              <p>
                {filtersApplied
                  ? 'No formulations match these filters.'
                  : 'No formulations found. Click "Add Formulation" to create one.'}
              </p>
            </div>
          ) : (
            <>
            {/* The outer table lists customers and their blend lines; the
                generations live in a nested table with a header of its own.
                They were one flat tbody so the columns would stay aligned, but
                the group row now carries genuinely different columns from a
                generation — a customer and a product are not the same shape,
                and one header cannot honestly label both. */}
            <table className="data-table formulations-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th className="fm-check-col">
                    <TickCheckbox
                      id="fm-select-all"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      ariaLabel="Select all"
                    />
                  </th>
                  <th>Customer</th>
                  <th>Blend line</th>
                  <th>Formulations</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              {/* Dots whenever a fetch is in flight, not only when the table
                  happens to be empty, and the header stays put rather than the
                  whole table being swapped for a spinner and back — the same
                  treatment Search POs uses. Stale rows fade so the previous
                  search's results cannot be mistaken for the new one's. */}
              <tbody className={loading ? 'loading-fade' : ''}>
                {loading && (
                  <tr>
                    <td colSpan={5}>
                      <LoadingSpinner />
                    </td>
                  </tr>
                )}
                {!loading && grouped.blends.map(blend => {
                  const isCollapsed = !expandedSet.has(blend.key);
                  const live = blend.generations.filter(
                    g => !g.item_archived && !g.is_locally_archived).length;
                  return (
                    <React.Fragment key={`blend-${blend.key}`}>
                      {/* The shared expandable-row shape (TableStyles.css), the
                          same as Search POs — the shadows and the darkened
                          expanded background come with it rather than being
                          re-invented here. */}
                      <tr className={`expandable-row fm-blend-header${blend.key === 'unassigned' ? ' fm-blend-unassigned' : ''}${isCollapsed ? '' : ' expanded'}`}
                          onClick={() => toggleCollapsed(blend.key)}
                          onContextMenu={(e) => {
                            if (blend.key === 'unassigned') return;
                            e.preventDefault();
                            e.stopPropagation();
                            setRenamingBlend(blend);
                          }}>
                        <td className="fm-check-col">
                          <span className="material-symbols-rounded fm-blend-caret">
                            {isCollapsed ? 'chevron_right' : 'expand_more'}
                          </span>
                        </td>
                        <td>
                          {/* Badge inline, left of the name — the shape every
                              other table in the system uses. A column of its
                              own bought nothing but a fixed 110px of gap. */}
                          <span className="name-with-badge">
                            {blend.accountNo && (
                              <EntityLink type="contact" id={blend.accountId} variant="badge"
                                          className="account-badge-inline">
                                {blend.accountNo}
                              </EntityLink>
                            )}
                            <EntityLink type="contact" id={blend.accountId}
                                        className="fm-blend-customer">
                              {blend.account || blend.name}
                            </EntityLink>
                          </span>
                        </td>
                        <td>
                          <span className="fm-blend-line">
                            {blend.key !== 'unassigned' && (
                              <span className="fm-blend-line-badge">{blend.name}</span>
                            )}
                            {/* The pencil sits with the name it renames, not
                                out in the actions column with the actions that
                                belong to the whole line. */}
                            <button
                              className={`btn-icon fm-blend-edit${blend.key === 'unassigned' ? ' btn-disabled' : ''}`}
                              disabled={blend.key === 'unassigned'}
                              title={blend.key === 'unassigned'
                                ? 'Unassigned is not a real blend line'
                                : 'Rename this blend line'}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (blend.key !== 'unassigned') setRenamingBlend(blend);
                              }}
                            >
                              <span className="material-symbols-rounded">edit</span>
                            </button>
                          </span>
                        </td>
                        <td>
                          <span className="fm-blend-count">
                            {blend.generations.length} formulation{blend.generations.length !== 1 ? 's' : ''}
                            {blend.key !== 'unassigned' && ` · ${live} active`}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="actions-cell">
                            {/* Unassigned is not a real blend, so there is
                                nothing to take the next generation from. */}
                            <button
                              className={`btn-icon${blend.key === 'unassigned' ? ' btn-disabled' : ''}`}
                              disabled={blend.key === 'unassigned'}
                              title={blend.key === 'unassigned'
                                ? 'Place these into a customer blend first'
                                : 'Start the next formulation from the newest one'}
                              onClick={() => { if (blend.key !== 'unassigned') setNewGenerationFor(blend); }}
                            >
                              <span className="material-symbols-rounded">add</span>
                            </button>
                          </div>
                        </td>
                      </tr>

                      <tr className={`expanded-content${isCollapsed ? '' : ' expanded'}`}>
                        <td colSpan={5}>
                          <div className="line-items-container">
                            <table className="line-items-table fm-generations">
                              <thead>{generationHead}</thead>
                              <tbody>
                                {blend.generations.map(row => renderRow(row))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Compounds have no customer to group under — they are the
                standing range, not one farm's recipe — so they get the
                generation header directly rather than an invented group. */}
            {grouped.loose.length > 0 && (
              <table className="data-table fm-generations fm-loose-table"
                     onContextMenu={ctxMenu.onTableContextMenu}>
                <thead>{generationHead}</thead>
                <tbody>
                  {grouped.loose.map(row => renderRow(row))}
                </tbody>
              </table>
            )}
            </>
          )}
        </div>
      </div>

      {organising && (
        <CustomerBlendOrganiser
          onClose={() => setOrganising(false)}
          onChanged={() => { refresh(); reloadCustomerBlends(); }}
        />
      )}

      {/* Renaming a line is not renaming the products in it — the item names
          are the supplier's and stay as they are. */}
      {renamingBlend && (
        <PromptDialog
          isOpen
          title={`Rename blend line \u2014 ${renamingBlend.account || ''}`.trim()}
          message="What this customer calls this line: Milkers, Beef, Youngstock. The formulations inside keep their own names."
          initialValue={renamingBlend.name}
          placeholder="e.g. Milkers"
          confirmLabel="Rename"
          onCancel={() => setRenamingBlend(null)}
          onConfirm={(value) => renameBlend(renamingBlend, value)}
        />
      )}

      {movingRow && (
        <MoveFormulationDialog
          row={movingRow}
          onClose={() => setMovingRow(null)}
          onMoved={() => { setMovingRow(null); refresh(); }}
        />
      )}

      {newGenerationFor && (
        <PromptDialog
          isOpen
          title={`New formulation — ${newGenerationFor.account || ''} ${newGenerationFor.name}`.trim()}
          message="Give it a working name. The supplier's code and final name are set when they reply."
          placeholder="e.g. CF Sept 26 Blend"
          confirmLabel="Create and edit"
          onCancel={() => setNewGenerationFor(null)}
          onConfirm={(value) => startNewGeneration(newGenerationFor, value)}
        />
      )}

      {confirmCodeFor && (
        <ConfirmSupplierCodeModal
          row={confirmCodeFor}
          onClose={() => setConfirmCodeFor(null)}
          onConfirmed={(data) => {
            setConfirmCodeFor(null);
            setAlertDialog({
              isOpen: true,
              type: 'success',
              message: `${data.item.item_code} · ${data.item.item_name}${data.createdInXero ? ' — created in Xero' : ''}`
            });
            refresh();
          }}
        />
      )}

      {openModal && (
        <FormulationModal
          mode={openModal.mode}
          formulationId={openModal.id}
          components={components}
          // No period: the modal opens on the product's newest entry, as the
          // row shows it.
          period={null}
          onComponentsChanged={fetchComponents}
          onClose={() => setOpenModal(null)}
          onSaved={() => { setOpenModal(null); refresh(); }}
          onIterationDeleted={refresh}
          onExport={(id, p) => { setExportPeriod(p || null); setExportIds([id]); }}
          newForCustomer={{
            onOpen: () => { setOpenModal(null); setNewForCustomer(true); },
            disabledReason: newForCustomerAccess.reason
          }}
        />
      )}

      {newForCustomer && (
        <NewCustomerFormulationModal
          onCreated={(data) => {
            // A Xero hold-up is explained by the modal itself; this is the
            // all-clear.
            if (data.xero?.pushed) {
              setAlertDialog({
                isOpen: true,
                type: 'success',
                message: `${data.item.item_code} · ${data.item.item_name} — created in Xero, `
                  + `filed under ${data.placement.account_name} · ${data.placement.line_name}`
              });
            }
          }}
          // Closing refreshes what is behind it, created or not.
          onClose={() => { setNewForCustomer(false); refresh(); reloadCustomerBlends(); }}
        />
      )}

      {exportIds && (
        <FormulationExportModal
          ids={exportIds}
          // From the modal: the period of the entry it was showing. From the
          // table: the current period (each sheet still falls back to its
          // product's newest entry, labelled with that entry's period).
          period={exportPeriod || currentPeriod}
          accountNames={exportAccountNames}
          onClose={() => { setExportIds(null); setExportPeriod(null); }}
          onDone={(msg) => {
            setExportIds(null);
            setExportPeriod(null);
            setAlertDialog({ isOpen: true, message: msg, type: 'success' });
          }}
        />
      )}

      {historyFor && (
        <AuditHistoryModal
          isOpen={true}
          onClose={() => setHistoryFor(null)}
          entityType="formulation"
          entityId={historyFor.id}
          entityIdentifier={`${historyFor.item_code} - ${historyFor.item_name}`}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        onConfirm={() => { if (confirmDialog.onConfirm) confirmDialog.onConfirm(); }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ isOpen: false, message: '', type: 'info' })}
      />

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={bulkActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add formulation', onClick: () => setOpenModal({ mode: 'add', id: null }) },
          { key: 'refresh', icon: 'refresh', label: 'Refresh', onClick: refresh },
          {
            // Archived items are always listed now, so this narrows TO them
            // rather than revealing them — a different question, and the label
            // has to say which one it is asking.
            key: 'only-archived',
            icon: 'inventory_2',
            readSafe: true,
            active: filters.showArchived,
            label: filters.showArchived ? 'Show all items' : 'Only archived items',
            onClick: () => handleFilterChange('showArchived', !filters.showArchived)
          }
        ]}
      />
    </div>
  );
}

export default Formulations;
